import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Plus, Save } from 'lucide-react';

import { DiscardChangesModal } from '@/features/roles/components/DiscardChangesModal';
import { RoleDetailView } from '@/features/roles/components/RoleDetailView';
import { RoleListView } from '@/features/roles/components/RoleListView';
import { SeedEditWarningModal } from '@/features/roles/components/SeedEditWarningModal';
import { useCreateRole, useUpdateRole } from '@/features/roles/hooks/use-role-mutations';
import { useRolesQuery } from '@/features/roles/hooks/use-roles';
import { useRoleDetailStore } from '@/features/roles/store/role-detail.store';
import { RoleApiError } from '@/features/roles/types/role.types';
import { isPermissionDenied } from '@/api/permission-error';
import { getRoleErrorMessage, isFieldNameError } from '@/features/roles/utils/error-messages';
import { isPermissionReduced } from '@/features/roles/utils/permission';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useRequirePermission } from '@/shared/hooks/useRequirePermission';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

const ROLE_NAME_REGEX = /^[A-Z0-9_]{2,30}$/;

type PendingExit = 'back' | 'cancel' | null;

export function RoleManagementPage() {
  const { t } = useTranslation('roles');

  // PRD §7: SYSTEM_MGMT:READ required to view; WRITE required to mutate.
  const canRead = useRequirePermission('SYSTEM_MGMT', 'READ');
  const { canWrite, denyProps, tooltip } = useWriteGuard('SYSTEM_MGMT');

  const mode = useRoleDetailStore((s) => s.mode);
  const currentRoleId = useRoleDetailStore((s) => s.currentRoleId);
  const draft = useRoleDetailStore((s) => s.draft);
  const snapshot = useRoleDetailStore((s) => s.snapshot);
  const openList = useRoleDetailStore((s) => s.openList);
  const openView = useRoleDetailStore((s) => s.openView);
  const openEdit = useRoleDetailStore((s) => s.openEdit);
  const openCreate = useRoleDetailStore((s) => s.openCreate);
  const setNameError = useRoleDetailStore((s) => s.setNameError);
  const isDirty = useRoleDetailStore((s) => s.isDirty);

  const { data: roles = [] } = useRolesQuery();
  const currentRole = currentRoleId != null ? roles.find((r) => r.id === currentRoleId) : null;

  const updateMutation = useUpdateRole();
  const createMutation = useCreateRole();
  const isSaving = updateMutation.isPending || createMutation.isPending;

  const [showSeedWarning, setShowSeedWarning] = useState(false);
  const [pendingExit, setPendingExit] = useState<PendingExit>(null);
  const [showPermReductionWarning, setShowPermReductionWarning] = useState(false);
  const [errorPopup, setErrorPopup] = useState<string | null>(null);

  if (!canRead) return null;

  const isEditingOrCreating = mode === 'edit' || mode === 'create';

  const performBack = () => openList();
  const performCancel = () => {
    if (mode === 'create') openList();
    else if (currentRole) openView(currentRole);
    else openList();
  };

  const handleBack = () => {
    if (isEditingOrCreating && isDirty()) {
      setPendingExit('back');
    } else {
      performBack();
    }
  };

  const handleCancel = () => {
    if (isEditingOrCreating && isDirty()) {
      setPendingExit('cancel');
    } else {
      performCancel();
    }
  };

  const confirmDiscard = () => {
    if (pendingExit === 'back') performBack();
    else if (pendingExit === 'cancel') performCancel();
    setPendingExit(null);
  };

  const handleEditClick = () => {
    if (!currentRole) return;
    if (currentRole.isSeed) {
      setShowSeedWarning(true);
    } else {
      openEdit(currentRole);
    }
  };

  const confirmSeedEdit = () => {
    if (!currentRole) return;
    setShowSeedWarning(false);
    openEdit(currentRole);
  };

  const performSave = async (trimmed: string) => {
    if (!draft) return;
    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: trimmed,
          permissionMap: draft.permissionMap,
        });
        toast.success(t('toasts.created', { name: trimmed }));
        openList();
      } else if (mode === 'edit' && currentRoleId != null) {
        const updated = await updateMutation.mutateAsync({
          id: currentRoleId,
          input: { name: trimmed, permissionMap: draft.permissionMap },
        });
        toast.success(t('toasts.updated', { name: trimmed }));
        // PRD §3.3.4: after a successful edit, return to the role's view mode.
        openView(updated);
      }
    } catch (e) {
      // Field-name errors stay inline on the name field; other failures surface
      // an operation-specific popup (PRD §8.2).
      if (e instanceof RoleApiError && isFieldNameError(e)) {
        setNameError(getRoleErrorMessage(e, t));
        return;
      }
      const fallback = mode === 'create' ? 'errors.createFailed' : 'errors.saveFailed';
      const message = getRoleErrorMessage(e, t, fallback);
      // SHCT-582 — PRD 21 §7.4 P-4: a server RBAC refusal is 토스트(error), 화면 유지. The §8.2
      // 팝업 keeps every other failure, which is what that clause is actually about.
      if (isPermissionDenied(e)) toast.error(message);
      else setErrorPopup(message);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    const trimmed = draft.name.trim();
    if (!ROLE_NAME_REGEX.test(trimmed)) {
      setNameError(trimmed.length === 0 ? t('errors.nameRequired') : t('errors.nameInvalid'));
      return;
    }
    setNameError(null);

    if (
      mode === 'edit' &&
      currentRole &&
      currentRole.adminCount > 0 &&
      snapshot &&
      isPermissionReduced(snapshot.permissionMap, draft.permissionMap)
    ) {
      setShowPermReductionWarning(true);
      return;
    }

    await performSave(trimmed);
  };

  const confirmPermReductionSave = async () => {
    setShowPermReductionWarning(false);
    if (!draft) return;
    await performSave(draft.name.trim());
  };

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('page.title')}</h1>
        </div>
        <div className="content__actions">
          {mode === 'list' && (
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={openCreate}
              disabled={!canWrite}
              {...denyProps}
              title={!canWrite ? t('tooltips.noWritePermission') : undefined}
            >
              <Plus size={16} />
              {t('actions.register')}
            </button>
          )}

          {mode !== 'list' && (
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleBack}
              disabled={isSaving}
            >
              <ArrowLeft size={16} />
              {t('actions.back')}
            </button>
          )}

          {mode === 'view' && currentRole && (
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleEditClick}
              disabled={currentRole.isReserved || !canWrite}
              {...denyProps}
              title={
                currentRole.isReserved
                  ? t('tooltips.rootEditBlocked')
                  : !canWrite
                  ? t('tooltips.noWritePermission')
                  : undefined
              }
            >
              <Pencil size={16} />
              {t('actions.edit')}
            </button>
          )}

          {isEditingOrCreating && (
            <>
              <button
                type="button"
                className="btn btn-default btn-primary"
                onClick={handleSave}
                disabled={isSaving || !canWrite}
                {...denyProps}
                title={!canWrite ? t('tooltips.noWritePermission') : undefined}
              >
                <Save size={16} />
                {isSaving ? t('actions.saving') : t('actions.save')}
              </button>
              <button
                type="button"
                className="btn btn-default btn-ghost"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {t('actions.cancel')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="content__body">
        {mode === 'list' && <RoleListView />}
        {mode !== 'list' && <RoleDetailView isRoot={currentRole?.isReserved ?? false} />}
      </div>

      <SeedEditWarningModal
        open={showSeedWarning}
        roleName={currentRole?.name ?? ''}
        onConfirm={confirmSeedEdit}
        onCancel={() => setShowSeedWarning(false)}
      />

      <DiscardChangesModal
        open={pendingExit !== null}
        onConfirm={confirmDiscard}
        onCancel={() => setPendingExit(null)}
      />

      <ConfirmModal
        open={showPermReductionWarning}
        title={t('modals.permReduction.title')}
        message={t('modals.permReduction.message')}
        confirmLabel={t('actions.continue')}
        cancelLabel={t('actions.cancel')}
        loading={isSaving}
        loadingLabel={t('actions.saving')}
        onConfirm={confirmPermReductionSave}
        onCancel={() => setShowPermReductionWarning(false)}
      />

      <InfoModal
        open={errorPopup !== null}
        title={t('modals.error.title')}
        body={errorPopup ?? ''}
        confirmLabel={t('actions.confirm')}
        onClose={() => setErrorPopup(null)}
      />
    </>
  );
}
